import React, { memo, useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { Box, FormControlLabel, IconButton, Switch, Typography, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useWindowScroll } from 'react-use';

import { SLOT_TIME_FORMAT_24H, SLOT_TIME_FORMAT_SMALL } from '../../../../../constants/dates.constants';
import TimezonesSelect from '../TimezonesSelect/TimezonesSelect';
import useResponsive from '../../../../../hooks/useResponsive';

const useStyles = makeStyles(({ breakpoints, palette, zIndex }) => ({
  arrowButton: {
    width: 40,
    height: 40,
    border: `1px solid ${palette.grey.dark}`,
  },
  switchLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: palette.grey.main,
  },
  icon: {
    fontSize: 20,
    fontWeight: 'bold',
    color: `${palette.text.primary} !important`,
  },
  mobileWrapper: {
    [breakpoints.down('sm')]: {
      position: 'sticky',
      backgroundColor: palette.common.white,
      top: 0,
      zIndex: zIndex.appBar,
    },
  },
  mobileWrapperBorder: {
    [breakpoints.down('sm')]: {
      boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.1)',
    },
  },
  mobileWeekPicker: {
    [breakpoints.down('sm')]: {
      marginBottom: 0,
    },
  },
  monthTitle: {
    color: palette.text.primary,
  },
}));

export interface IHeaderProps {
  from: string;
  to: string;
  isLoading?: boolean;
  onChangeWeek: (range: { from: moment.Moment; to: moment.Moment }) => void;
  onChangeTimezone: (timezone: { timezoneName: string }) => void;
  onToggleSlotsFormat: (format: string) => void;
}

const Header = ({ from, to, isLoading = false, onChangeWeek, onChangeTimezone, onToggleSlotsFormat }: IHeaderProps) => {
  const classes = useStyles();

  const [isAmPmFormat, setIsAmPmFormat] = useState(true);
  const [isTimezoneVisible, setIsTimezoneVisible] = useState(true);

  const { y: scrollY } = useWindowScroll();
  const { isMobile } = useResponsive();

  const fromDate = moment(from);
  const toDate = moment(to);
  const today = moment.utc();
  const isInOneMonth = fromDate.month() === toDate.month();
  const toDateFormat = isInOneMonth ? 'D' : 'MMM D';
  const fromDateFormat = isInOneMonth ? 'MMMM D' : 'MMM D';
  const isPreviousAvailable = today.clone().startOf('isoWeek').isSameOrBefore(fromDate);
  const isNextAvailable = true;

  const getWeekDates = useCallback(
    (direction = 'forward') => {
      const days = direction === 'forward' ? 7 : -7;
      return {
        from: fromDate.clone().add(days, 'days'),
        to: toDate.clone().add(days, 'days'),
      };
    },
    [fromDate, toDate],
  );

  const scrollToDates = useCallback(() => {
    if (isMobile && scrollY > 300) {
      window.scrollTo({
        top: 56,
        behavior: 'smooth',
      });
    }
  }, [scrollY, isMobile]);

  const handleClickNextWeek = useCallback(() => {
    onChangeWeek(getWeekDates());
    scrollToDates();
  }, [onChangeWeek, getWeekDates, scrollToDates]);
  const handleClickPrevWeek = useCallback(() => {
    onChangeWeek(getWeekDates('backward'));
    scrollToDates();
  }, [onChangeWeek, getWeekDates, scrollToDates]);

  const toggleSlotsTimeFormat = useCallback(() => {
    const toggledFormat = !isAmPmFormat;
    const slotsTimeFormat = toggledFormat ? SLOT_TIME_FORMAT_SMALL : SLOT_TIME_FORMAT_24H;
    setIsAmPmFormat(toggledFormat);
    onToggleSlotsFormat(slotsTimeFormat);
  }, [isAmPmFormat, onToggleSlotsFormat]);

  const period = (
    <Box component="span" mx={2}>
      {isLoading ? (
        <Box width={1} mb={2} data-testid="Calendar.Week.TitleLoading">
          <Skeleton width={126} height={24} />
        </Box>
      ) : (
        <Typography
          data-date={fromDate.format('YYYY-MM-DD')}
          data-testid="Calendar.Week.Title"
          className={classNames('H4-Dark-Bold', classes.monthTitle)}
        >
          {fromDate.format(fromDateFormat)} - {toDate.format(toDateFormat)}
        </Typography>
      )}
    </Box>
  );

  useEffect(() => {
    setIsTimezoneVisible(scrollY === 0);
  }, [scrollY]);

  return (
    <Box
      className={classNames(classes.mobileWrapper, { [classes.mobileWrapperBorder]: !isTimezoneVisible })}
      width={1}
      py={2}
      px={[3, 0]}
      display="flex"
      alignItems="center"
      justifyContent={['space-around', 'space-between']}
      flexDirection={['column', 'row']}
    >
      <Box
        className={classNames({ [classes.mobileWeekPicker]: !isTimezoneVisible })}
        width={[1, 'auto']}
        ml={[2, 4]}
        mr={[2, 4]}
        mb={[2, 0]}
        display="flex"
        justifyContent={['space-between', 'flex-start']}
        alignItems="center"
      >
        <Box mr={[0, 2]}>
          <IconButton
            data-testid="Calendar.PreviousWeek.Button"
            className={classes.arrowButton}
            disabled={!isPreviousAvailable}
            onClick={handleClickPrevWeek}
            size="large"
          >
            <ArrowBack className={classNames(classes.icon, 'arrowButton-ie')} />
          </IconButton>
        </Box>
        {isMobile && <Box component="span">{period}</Box>}
        <IconButton
          data-testid="Calendar.NextWeek.Button"
          className={classes.arrowButton}
          disabled={!isNextAvailable}
          onClick={handleClickNextWeek}
          size="large"
        >
          <ArrowForward className={classNames(classes.icon, 'arrowButton-ie')} />
        </IconButton>
        {!isMobile && <Box component="span">{period}</Box>}
      </Box>
      {isTimezoneVisible && (
        <Box
          width={[1, 'auto']}
          mr={[0, 4]}
          display="flex"
          flexDirection={['column-reverse', 'row']}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Box className="timeFormatCheckbox-ie" display="flex" alignItems="center" mt={[2, 0]}>
            <Box component="span" fontSize={12} fontWeight="bold" color="grey.main" fontFamily="Lato" mr={2}>
              AM/PM
            </Box>
            <FormControlLabel
              data-testid="Calendar.TimeFormat.Switcher"
              classes={{ label: classes.switchLabel }}
              control={
                <Switch
                  size="small"
                  color="primary"
                  checked={!isAmPmFormat}
                  disabled={isLoading}
                  onChange={toggleSlotsTimeFormat}
                />
              }
              label="24HR"
            />
          </Box>
          <Box width={1}>
            <TimezonesSelect isLoading={isLoading} onSelectTimezone={onChangeTimezone} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default memo(Header);
